/**
 * AWS S3 uploader using AWS Signature Version 4.
 * Uses standard AWS S3 endpoints (bucket.s3.region.amazonaws.com).
 */

function toHex(buffer: ArrayBuffer): string {
  return Array.from(new Uint8Array(buffer))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function toUtf8Bytes(str: string): Uint8Array {
  return new TextEncoder().encode(str);
}

async function sha256(data: ArrayBuffer | Uint8Array): Promise<ArrayBuffer> {
  const view = data instanceof Uint8Array ? data : new Uint8Array(data);
  return crypto.subtle.digest("SHA-256", view as BufferSource);
}

function toArrayBuffer(key: ArrayBuffer | Uint8Array): ArrayBuffer {
  if (key instanceof ArrayBuffer) return key;
  const slice = key.buffer.slice(key.byteOffset, key.byteOffset + key.byteLength);
  return slice as ArrayBuffer;
}

async function hmacSha256(
  key: ArrayBuffer | Uint8Array,
  data: string | ArrayBuffer | Uint8Array
): Promise<ArrayBuffer> {
  const keyBuffer = toArrayBuffer(key);
  const dataBytes =
    typeof data === "string"
      ? toUtf8Bytes(data)
      : data instanceof ArrayBuffer
      ? new Uint8Array(data)
      : data;
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBuffer,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"]
  );
  return crypto.subtle.sign("HMAC", cryptoKey, dataBytes as BufferSource);
}

function formatAmzDate(date: Date): string {
  const pad = (n: number, len: number) => n.toString().padStart(len, "0");
  return (
    `${pad(date.getUTCFullYear(), 4)}` +
    `${pad(date.getUTCMonth() + 1, 2)}` +
    `${pad(date.getUTCDate(), 2)}T` +
    `${pad(date.getUTCHours(), 2)}` +
    `${pad(date.getUTCMinutes(), 2)}` +
    `${pad(date.getUTCSeconds(), 2)}Z`
  );
}

export interface AwsS3UploaderConfig {
  accessKey: string;
  secretKey: string;
  bucket: string;
  region: string;
}

export class AwsS3Uploader {
  private accessKey: string;
  private secretKey: string;
  private bucket: string;
  private region: string;

  constructor(config: AwsS3UploaderConfig) {
    this.accessKey = config.accessKey;
    this.secretKey = config.secretKey;
    this.bucket = config.bucket;
    this.region = config.region;
  }

  private getHost(): string {
    return `${this.bucket}.s3.${this.region}.amazonaws.com`;
  }

  private async getSigningKey(
    dateStamp: string,
    region: string,
    service: string
  ): Promise<ArrayBuffer> {
    const kSecret = await hmacSha256(
      toUtf8Bytes(`AWS4${this.secretKey}`),
      dateStamp
    );
    const kRegion = await hmacSha256(kSecret, region);
    const kService = await hmacSha256(kRegion, service);
    const kSigning = await hmacSha256(kService, "aws4_request");
    return kSigning;
  }

  async uploadFile(
    file: File,
    options: {
      serviceName: string;
      contentType?: string;
    }
  ): Promise<string | null> {
    const { serviceName, contentType = "application/octet-stream" } = options;

    try {
      const extension = (file.name.split(".").pop() || "").toLowerCase();
      const recordId = Date.now().toString();
      const imageExtensions = ["jpg", "jpeg", "png", "gif", "webp"];
      const typeFolder = imageExtensions.includes(extension) ? "images" : "files";
      const originalName = file.name.replace(/^.*[\\/]/, "");
      const fileName = `${serviceName}/${recordId}/${typeFolder}/${Date.now()}_${originalName}`;

      const host = this.getHost();
      const url = `https://${host}/${fileName}`;

      const bytes = new Uint8Array(await file.arrayBuffer());
      const now = new Date();
      const amzDate = formatAmzDate(now);
      const dateStamp = amzDate.substring(0, 8);

      const payloadHashBuf = await sha256(bytes);
      const payloadHash = toHex(payloadHashBuf);

      const canonicalHeaders = `host:${host}\nx-amz-content-sha256:${payloadHash}\nx-amz-date:${amzDate}\n`;
      const signedHeaders = "host;x-amz-content-sha256;x-amz-date";
      const canonicalRequest = `PUT\n/${fileName}\n\n${canonicalHeaders}\n${signedHeaders}\n${payloadHash}`;

      const algorithm = "AWS4-HMAC-SHA256";
      const credentialScope = `${dateStamp}/${this.region}/s3/aws4_request`;
      const canonicalRequestHash = await sha256(toUtf8Bytes(canonicalRequest));
      const stringToSign = `${algorithm}\n${amzDate}\n${credentialScope}\n${toHex(canonicalRequestHash)}`;

      const signingKey = await this.getSigningKey(
        dateStamp,
        this.region,
        "s3"
      );
      const signatureBuf = await hmacSha256(signingKey, stringToSign);
      const signature = toHex(signatureBuf);

      const authorizationHeader = `${algorithm} Credential=${this.accessKey}/${credentialScope}, SignedHeaders=${signedHeaders}, Signature=${signature}`;

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": contentType,
          "x-amz-date": amzDate,
          "x-amz-content-sha256": payloadHash,
          Authorization: authorizationHeader,
        },
        body: bytes,
      });

      if (response.ok) {
        return url;
      }
      return null;
    } catch {
      return null;
    }
  }
}
